import { featuredImage } from "~/modules/images";
import { createComponent } from "react-fela";
import { getResourceSlugFromType } from "~/modules/link-parser";
import { textStyles } from "~/styleguide/typography";
import { colors } from "~/styleguide";
import { FelaComponent } from "react-fela";
import FixedHeightExcerpt from "~/components/shared/fixed-height-excerpt";
import get from "lodash.get";
import WordpressLink from "~/components/global/links";
import Visual from "~/components/shared/visual";
import readTime from "~/modules/read-time";
import TaxonomyList from "~/components/shared/taxonomy/list";
import { combineRules } from "fela";
import { fluid } from "~/styleguide/utils";

export default ({ resource, isFeature, type }) => {
    const image = featuredImage(resource);
    const typeURL = getResourceSlugFromType(resource.type || type);
    const excerpt = get(resource, "excerpt.rendered");
    const externalURL = get(resource, "external_url");
    const eyebrowText = readTime(resource);
    const hasImage = image ? true : false;

    const dateISO = new Date(get(resource, "date")).toISOString();
    const dateString = new Date(dateISO);
    const dateOptions = { month: "long", day: "numeric", year: "numeric" };
    const dateText = new Intl.DateTimeFormat("en-US", dateOptions).format(dateString);

    return (
        <PostSummary isFeature>
            <PostHeading isFeature>
                {/* Image */}

                <PostImageWrap isFeature={isFeature}>
                    <WordpressLink
                        url={externalURL || `/${typeURL}`}
                        slug={resource.slug}
                        skipLabel
                        isFeature={isFeature}
                        elem="div"
                    >
                        {image && (
                            <Visual
                                image={image}
                                aspect={16 / 9}
                                background="cover"
                            />
                        )}
                    </WordpressLink>
                </PostImageWrap>

                <FelaComponent
                    isFeature={isFeature}
                    hasImage={hasImage}
                    style={postHeadingRightStyle}
                >
                    <FelaComponent
                        style={combineRules(postTimeStyle,postMetaWrapStyle)}
                        as="div"
                    >
                        {eyebrowText && (
                            <FelaComponent as="span" style={textStyles.tag}>
                                {eyebrowText} - {dateText}
                            </FelaComponent>
                        )}

                        {isFeature && (
                            <>
                                <TaxonomyList
                                    extend={{ display: "inline-block" }}
                                    resource={resource}
                                />
                            </>
                        )}
                    </FelaComponent>

                    {/* Title */}
                    <WordpressLink
                        url={externalURL || `/${typeURL}`}
                        slug={resource.slug}
                        label={resource.title.rendered}
                        truncate
                        maxLines={3}
                        className="post-summary-title"
                        extend={combineRules(
                            textStyles.resourceHeading,
                            postHeadingStyle(isFeature)
                        )}
                    />

                    {/* Categories */}
                    {!isFeature && (
                        <FelaComponent
                            style={combineRules(
                                postMetaWrapStyle,
                                postMetaWrapFooterStyle
                            )}
                            as="div"
                        >
                            <TaxonomyList resource={resource} />
                        </FelaComponent>
                    )}
                </FelaComponent>
            </PostHeading>

            {/* Excerpt */}
            {excerpt && (
                <FixedHeightExcerpt
                    text={excerpt}
                    lineHeight={1.5}
                    maxLines={3}
                    extend={combineRules(
                        textStyles.paragraphLarge,
                        postExcerptStyle
                    )}
                ></FixedHeightExcerpt>
            )}
        </PostSummary>
    );
};

const PostSummary = createComponent(() => ({
    borderBottom: "1px solid #979797",
    paddingBottom: 30,
    ":not(:first-of-type)": {
        paddingTop: 30
    },
    "& .post-summary-title": {
        maxWidthTablet: {
            width: "100%"
        }
    }
}));

const PostImageWrap = createComponent(({ isFeature }) => ({
    width: "50%",
    height: "100%",
    borderRadius: 6,
    overflow: "hidden",
    display: "block",
    "& .vv-visual": {
        borderRadius: 6
    },
    extend: [
        {
            condition: isFeature,
            style: {
                width: "100%",
                marginBottom: 30
            }
        },
        {
            condition: !isFeature,
            style: {
                maxWidthTablet: {
                    width: "40%"
                }
            }
        }
    ]
}));

const PostHeading = createComponent(({ isFeature }) => ({
    width: "100%",
    display: "flex",
    justifyContent: "space-between",
    extend: [
        fluid("margin-bottom", { max: 25, min: 10 }),
        {
            condition: isFeature,
            style: {
                flexWrap: "wrap"
            }
        }
    ]
}));

const postHeadingStyle = isFeature => ({
    color: colors.black,
    display: "inline-block",
    marginBottom: 10,
    extend: [
        {
            condition: isFeature,
            style: textStyles.heading3
        }
    ]
});

const postHeadingRightStyle = props => ({
    width: "100%",
    display: "flex",
    flexWrap: "wrap",
    justifyContent: "flex-start",
    flexDirection: "column",

    extend: [
        {
            condition: props.hasImage,
            style: {
                width: "calc(50% - 30px)"
            }
        },
        {
            condition: props.isFeature,
            style: {
                width: "100%"
            }
        },
        {
            condition: props.hasImage && !props.isFeature,
            style: {
                maxWidthTablet: {
                    width: "calc(60% - 15px)"
                }
            }
        }
    ]
});

const postTimeStyle = {
    display: "flex",
    justifyContent: "space-between"
};

const postMetaWrapStyle = {
    width: "100%",
    minWidthTablet: { marginBottom: 15 }
};

const postMetaWrapFooterStyle = {
    marginTop: "auto",
    marginBottom: 0
};

const postExcerptStyle = {
    extend: [fluid("margin-top", { max: 30, min: 10 })]
};
